/**
 * Strings.
 *
 * English and Puerto Rican Spanish are both first-class from the first commit.
 * Retrofitting i18n means finding every string already inlined in a component,
 * and on this account that has meant a translation layer that only ever covered
 * the pages someone remembered.
 *
 * `es-PR`, not generic `es`: the wording is Puerto Rican, and the distinction
 * matters to the people reading it.
 */

export type Locale = 'en' | 'es-PR'

const STRINGS = {
  en: {
    "auth.setupOnly": "— only for the first owner account",
    "auth.noAccount": "No account?",
    "auth.haveAccount": "Already have one?",
    "auth.noPassword": "There is no password.",
    "auth.externalPasskey": "Use a security key, your password manager, or your phone.",
    "auth.passkeyOnly": "Your passkey — Face ID, a fingerprint, your password manager or a security key — is the only way in.",
    "notify.markRead": "Mark read",
    "error.reported": "This has been reported. Reloading usually helps.",
    "error.reload": "Reload",
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.loading': 'Loading…',

    'auth.signInIntro': 'Continue with your passkey.',
    'auth.createIntro': 'Create an account to apply for work or hire local people.',
    'auth.handle': 'Username',
    'auth.handlePlaceholder': 'yourname',
    'auth.displayName': 'Your name',
    'auth.createAccount': 'Create account',
    'auth.usePasskey': 'Sign in with a passkey',
    /* The delegate way in: a shared device, or an app store's reviewer, who
       cannot enrol a passkey on our authenticator. See lib/auth.ts. */
    'auth.usePin': 'Use a PIN instead',
    'auth.usePasskeyInstead': 'Use a passkey instead',
    'auth.pin': 'PIN',
    'auth.pinHint': 'A PIN account is created for you by somebody who already has one. It cannot reach the admin console, and it locks after five wrong tries.',
    'auth.noPasskeySupport': 'This browser cannot use passkeys. Try Chrome, Safari or Edge.',
    'auth.setupToken': 'Setup token',
    'auth.cancelled': 'That was cancelled. Nothing has changed.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Add a passkey',
    'account.removePasskey': 'Remove',
    'account.synced': 'Synced to your password manager',
    'account.deviceOnly': 'This device only',
    'account.neverUsed': 'Never used',
    'account.lastUsed': 'Last used',
    'account.recoveryWarning':
      'This account has one passkey and it is not synced. Lose that device and the account cannot be recovered — add a second passkey.',

    'notify.enable': 'Turn on notifications',
    'notify.enabled': 'Notifications are on',
    'notify.blocked': 'Notifications are blocked in your browser settings.',
    'notify.nativeBlocked': 'Allow notifications in Settings → Notifications → Vieques Worx, then return here.',
    'notify.checkPermission': 'Check notification permission',
    'notify.nativeRetry': 'Could not register this device. Check your connection and try again.',
    'notify.logoutRetry': 'Could not finish signing out. Check your connection and try again.',
    'notify.inbox': 'Notifications',
    'notify.empty': 'Nothing yet.',

    'admin.title': 'Admin',
    'admin.users': 'Users',
    'admin.audit': 'Activity',
    'admin.errors': 'Errors',
    'admin.flags': 'Flags',
    'admin.settings': 'Settings',
    'admin.broadcast': 'Broadcast',
    'admin.devices': 'Devices',

    'env.beta': 'BETA — this is not the live app',
    'env.local': 'LOCAL',

    'error.generic': 'Something went wrong.',
    'error.offline': 'You appear to be offline.',

    'update.ready': 'A newer version is ready',
    'update.now': 'Update now',
    'update.later': 'Later',

    'install.title': 'Put this on your home screen',
    'install.why': 'It gets its own icon and opens without the browser bar.',
    'install.ios': 'Tap Share, then “Add to Home Screen”.',
    'install.add': 'Add it',
    'install.no': 'No thanks',
  },

  'es-PR': {
    "auth.setupOnly": "— solo para la primera cuenta del dueño",
    "auth.noAccount": "¿No tienes cuenta?",
    "auth.haveAccount": "¿Ya tienes una?",
    "auth.noPassword": "No hay contraseña.",
    "auth.externalPasskey": "Usa una llave de seguridad, tu gestor de contraseñas o tu teléfono.",
    "auth.passkeyOnly": "Tu passkey — Face ID, una huella, tu gestor de contraseñas o una llave de seguridad — es la única forma de entrar.",
    "notify.markRead": "Marcar como leída",
    "error.reported": "Se reportó el problema. Volver a cargar suele ayudar.",
    "error.reload": "Volver a cargar",
    'app.signIn': 'Entrar',
    'app.signOut': 'Salir',
    'app.loading': 'Cargando…',

    'auth.signInIntro': 'Continúa con tu llave de acceso.',
    'auth.createIntro': 'Crea una cuenta para solicitar trabajos o contratar gente local.',
    'auth.handle': 'Usuario',
    'auth.handlePlaceholder': 'tunombre',
    'auth.displayName': 'Tu nombre',
    'auth.createAccount': 'Crear cuenta',
    'auth.usePasskey': 'Entrar con passkey',
    'auth.usePin': 'Usar un PIN',
    'auth.usePasskeyInstead': 'Usar un passkey',
    'auth.pin': 'PIN',
    'auth.pinHint': 'Una cuenta con PIN te la crea alguien que ya tiene una. No llega a la consola de administración y se bloquea a los cinco intentos fallidos.',
    'auth.noPasskeySupport': 'Este navegador no puede usar passkeys. Prueba Chrome, Safari o Edge.',
    'auth.setupToken': 'Código de configuración',
    'auth.cancelled': 'Se canceló. No cambió nada.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Añadir un passkey',
    'account.removePasskey': 'Quitar',
    'account.synced': 'Sincronizado con tu gestor de contraseñas',
    'account.deviceOnly': 'Solo en este equipo',
    'account.neverUsed': 'Nunca se ha usado',
    'account.lastUsed': 'Último uso',
    'account.recoveryWarning':
      'Esta cuenta tiene un solo passkey y no está sincronizado. Si pierdes ese equipo, no se puede recuperar la cuenta — añade un segundo passkey.',

    'notify.enable': 'Activar notificaciones',
    'notify.enabled': 'Las notificaciones están activadas',
    'notify.blocked': 'Las notificaciones están bloqueadas en tu navegador.',
    'notify.nativeBlocked': 'Permite las notificaciones en Ajustes → Notificaciones → Vieques Worx y vuelve aquí.',
    'notify.checkPermission': 'Comprobar permiso de notificaciones',
    'notify.nativeRetry': 'No se pudo registrar este dispositivo. Revisa tu conexión e inténtalo de nuevo.',
    'notify.logoutRetry': 'No se pudo cerrar la sesión. Revisa tu conexión e inténtalo de nuevo.',
    'notify.inbox': 'Notificaciones',
    'notify.empty': 'Nada todavía.',

    'admin.title': 'Administración',
    'admin.users': 'Usuarios',
    'admin.audit': 'Actividad',
    'admin.errors': 'Errores',
    'admin.flags': 'Flags',
    'admin.settings': 'Ajustes',
    'admin.broadcast': 'Mensaje',
    'admin.devices': 'Equipos',

    'env.beta': 'BETA — esta no es la app en vivo',
    'env.local': 'LOCAL',

    'error.generic': 'Algo salió mal.',
    'error.offline': 'Parece que no tienes conexión.',

    'update.ready': 'Hay una versión más nueva',
    'update.now': 'Actualizar',
    'update.later': 'Ahora no',

    'install.title': 'Ponlo en tu pantalla de inicio',
    'install.why': 'Tiene su propio ícono y abre sin la barra del navegador.',
    'install.ios': 'Toca Compartir y luego “Añadir a inicio”.',
    'install.add': 'Añadir',
    'install.no': 'Ahora no',
  },
} as const

export type StringKey = keyof (typeof STRINGS)['en']

let locale: Locale = detect()

function detect(): Locale {
  const stored = localStorage.getItem('locale')
  if (stored === 'en' || stored === 'es-PR') return stored
  return navigator.language.toLowerCase().startsWith('es') ? 'es-PR' : 'en'
}

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  locale = next
  localStorage.setItem('locale', next)
  document.documentElement.lang = next
}

/**
 * A missing translation falls back to English rather than rendering the key.
 * A user seeing `account.passkeys` learns nothing; a user seeing the English
 * learns what the control does.
 */
export function t(key: StringKey): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key
}
